import buildStateFacets from "./buildStateFacets";
function buildTotalPages(resultsPerPage, totalResults) {
    if (!resultsPerPage)
        return 0;
    if (totalResults === 0)
        return 1;
    return Math.ceil(totalResults / resultsPerPage);
}
function buildTotalResults(hits) {
    return hits.total.value;
}
function getHighlight(hit, fieldName) {
    if (!hit.highlight || !hit.highlight[fieldName] || hit.highlight[fieldName].length < 1) {
        return;
    }
    return hit.highlight[fieldName][0];
}
/**
 * Builds the results array from the hits returned by Elasticsearch
 * We also add the redirect URL for each result if linkUrl is provided (for tracking)
 *
 * @param hits - array of search result hits
 * @param redirectURL - base URL for redirection
 * @returns - array of formatted results
 */
function buildResults(hits, linkUrl) {
    /**
     * Add each key-value pair to an object
     * Done to satisfy TypeScript requirements
     *
     * @param prev - previous object
     * @param current - current key-value pair
     * @param index - index in the array
     * @param array - the entire array
     * @returns - new object with added key-value pair (e.g. { title: ..., url: ... })
     */
    const addEachKeyValueToObject = (prev, current, index, array) => ({
        ...prev,
        [current[0]]: current[1],
    });
    /**
     * Helpers to convert field value to FieldValueWrapper
     * Makes sure to add redirect URL if applicable
     *
     * @param fieldName - name of the field (e.g., "url", "title")
     * @param value - the actual field value
     * @param index - index of the result in the hits array (for tracking)
     * @param snippet - highlighted snippet for the field, if available
     * @returns - FieldValueWrapper object
     */
    const toResultFieldObject = (fieldName, value, index, snippet) => {
        return {
            raw: value,
            ...((fieldName === "url" && linkUrl) && { withRedirect: `${linkUrl}${encodeURI(value)}&idx=${index}` }),
            ...(snippet && { snippet })
        };
    };
    return hits.map((record, index) => {
        return Object.entries(record._source)
            .map(([fieldName, fieldValue]) => [
            fieldName,
            toResultFieldObject(fieldName, fieldValue, index, getHighlight(record, fieldName)),
        ])
            .reduce(addEachKeyValueToObject, {});
    });
}
/*
  Converts an Elasticsearch response to new application state

  When implementing an onSearch Handler in Search UI, the handler needs to convert
  search results into a new application state that Search UI understands.

  For instance, Elasticsearch returns "hits" for search results. This maps to
  the "results" property in application state, which requires a specific format. So this
  file iterates through "hits" and reformats them to "results" that Search UI
  understands.

  We do similar things for facets and totals.
*/
export default function buildState(response, facetNames, resultsPerPage) {
    const results = response.hits ? buildResults(response.hits.hits, response.linkUrl) : [];
    const totalResults = response.hits ? buildTotalResults(response.hits) : 0;
    const totalPages = response ? buildTotalPages(resultsPerPage, totalResults) : 0;
    const facets = !!response && buildStateFacets(response.aggregations, facetNames);
    return {
        results,
        totalPages,
        totalResults,
        ...(facets && { facets }),
    };
}
